import type {
  InventoryListing,
  Account,
  BankAccount,
} from "@workspace/api-client-react";
import { effectivePrice, formatMoney } from "@/components/inventory-cells";
import { linkCoverageStats } from "@/lib/link-coverage";

// Ordered column labels for the inventory CSV export. The order here is the
// column order in the produced file.
const EXPORT_COLUMNS = [
  "Event",
  "Section",
  "Row",
  "Seats",
  "Qty",
  "List Price",
  "Recommended Price",
  "Account Email",
  "Puller",
  "Renter",
  "Phone Status",
  "Bank Status",
  "FVE Status",
  "Sales Status",
  "QR",
  "Links",
  "Reach Listing ID",
  "Skybox Listing ID",
  "Internal Notes",
] as const;

export type InventoryExportRow = Record<(typeof EXPORT_COLUMNS)[number], string>;

// Just the seat range portion of a listing (Section / Row are their own
// columns, so unlike seatSummary we don't prefix them here).
function seatsOnly(row: InventoryListing): string {
  const s = row.seatsDisplay?.trim();
  if (s) return s;
  if (row.seatFrom) {
    return row.seatTo && row.seatTo !== row.seatFrom
      ? `${row.seatFrom}-${row.seatTo}`
      : `${row.seatFrom}`;
  }
  return "";
}

// Plain-English FVE status label. Mirrors the pill labels shown in the table so
// the export reads the same as the screen.
function fveStatusLabel(row: InventoryListing): string {
  const status = row.fveStatus ?? "eligible";
  switch (status) {
    case "sold":
      return "Sold";
    case "sold_partial":
      return "Sold Partial";
    case "sale_cancelled":
      return "Sale Cancelled";
    case "cannot_list":
      return "Cannot List";
    case "surrender_on_fve":
      return "Surrender";
    case "via_reach":
      return "Selling via Reach";
    case "via_skybox":
      return "Via Skybox";
    case "listed":
    case "listed_discount":
      return "Listed";
    case "held":
      switch (row.holdSubState) {
        case "failed":
          return "Held · Failed";
        case "expired":
          return "Held · Expired";
        case "expiring":
          return "Held · Expiring";
        default:
          return "Held";
      }
    default:
      return "Eligible";
  }
}

// Short sales summary derived from the confirmed-sale counts the row already
// carries (the live Tickets-column pips are derived per-row, so we use the
// already-present counts instead to keep the export client-side).
function salesStatusLabel(row: InventoryListing): string {
  const sales = row.confirmedSaleCount ?? 0;
  if (sales <= 0) return "";
  const tickets = row.soldTicketCount ?? 0;
  const salePart = `${sales} sale${sales === 1 ? "" : "s"}`;
  return tickets > 0 ? `${salePart} (${tickets} tickets)` : salePart;
}

// Bank status label, matching the table's Bank column: a descriptive bank name
// when mapped, otherwise "No Bank".
function bankStatusLabel(row: InventoryListing): string {
  if (!row.bankMappingStatus) return "No Bank";
  return row.bankCardName || row.bankName || row.bankSource || "Bank";
}

// The displayed (effective) list price formatted with its currency symbol.
function listPriceLabel(row: InventoryListing): string {
  const eff = effectivePrice(row);
  if (eff.value == null) return "";
  return formatMoney(eff.value, row.currencyCode);
}

// The admin-set recommended price, only when one has actually been set (the
// field defaults to face value server-side, so an unset recommendation is
// blank rather than echoing face value).
function recommendedPriceLabel(row: InventoryListing): string {
  if (row.recommendedListPriceSetBy == null || row.recommendedListPrice == null) {
    return "";
  }
  return formatMoney(row.recommendedListPrice, row.currencyCode);
}

// QR/PDF coverage label, mirroring the QR column in the table: "Full" when
// every (unsold) ticket has a PDF in Reach, "Partial" when only some do, and
// "None" when no ticket has a PDF yet. Sold tickets never carry a PDF, so full
// coverage is measured against unsold tickets when any sale exists (matching
// QrCoverageCell). The "X/Y" count is appended so the export is self-explanatory.
function qrCoverageLabel(row: InventoryListing): string {
  const assets = row.ticketAssets ?? [];
  const withPdf = assets.filter((a) => a.hasPdf).length;
  if (withPdf === 0) return "None";
  const hasSold = assets.some((a) => a.sold);
  const denominator = hasSold
    ? assets.filter((a) => !a.sold).length
    : assets.length;
  const label = withPdf >= denominator ? "Full" : "Partial";
  return `${label} (${withPdf}/${denominator})`;
}

// Ticketransfer link coverage label, mirroring the Links column for domestic
// events: "Full" when every relevant ticket has a transfer link, "Partial" when
// only some do, and "" (blank) when this listing carries no link cache at all
// (i.e. an international event or a domestic event with no imported tasks yet),
// so the column reads cleanly for the QR-delivered majority. Numerator and
// denominator come from the shared helper so the export never drifts from the
// in-app Links cell (including buy-back rows, whose sold tickets need links).
function linkCoverageLabel(row: InventoryListing): string {
  const { assets, withLink, denominator } = linkCoverageStats(row);
  if (assets.length === 0) return "";
  if (withLink === 0) return "None";
  const label = withLink >= denominator ? "Full" : "Partial";
  return `${label} (${withLink}/${denominator})`;
}

// Map a listing into the flat, labelled shape used for the CSV. Nulls become
// empty strings so blank cells read cleanly.
export function inventoryToExportRow(row: InventoryListing): InventoryExportRow {
  return {
    Event: row.eventName ?? "",
    Section: row.section ?? "",
    Row: row.row ?? "",
    Seats: seatsOnly(row),
    Qty: String(row.buybackCandidate ? (row.ticketCount ?? 0) : (row.availableQuantity ?? 0)),
    "List Price": listPriceLabel(row),
    "Recommended Price": recommendedPriceLabel(row),
    "Account Email": row.vendorAccountEmail ?? "",
    Puller: row.pullerName ?? "",
    Renter: row.historicalJuiceRenterName ?? "",
    "Phone Status": row.phoneStatus ?? "unknown",
    "Bank Status": bankStatusLabel(row),
    "FVE Status": fveStatusLabel(row),
    "Sales Status": salesStatusLabel(row),
    QR: qrCoverageLabel(row),
    Links: linkCoverageLabel(row),
    "Reach Listing ID": row.reachListingId ?? "",
    // No per-listing Skybox ID is exposed by the API today; the column is kept
    // for a stable schema and filled if/when that field is added.
    "Skybox Listing ID": "",
    "Internal Notes": row.internalNotes ?? "",
  };
}

// Build the full set of export rows for a list of listings, dropping the
// coverage column that the on-screen grid would also hide. The visibility rule
// mirrors the inventory tables exactly: the QR column shows only when at least
// one international row is present, and the Links column only when at least one
// domestic row is present. A genuinely mixed export (account view, global
// search) keeps both; a single-type list keeps just its one relevant column so
// the file matches what the user saw.
export function buildInventoryExportRows(
  listings: InventoryListing[],
): InventoryExportRow[] {
  const showQr = listings.some((r) => r.marketType === "international");
  const showLink = listings.some((r) => r.marketType === "domestic");
  return listings.map((listing) => {
    const full = inventoryToExportRow(listing);
    if (showQr && showLink) return full;
    const row: Partial<InventoryExportRow> = {};
    for (const col of EXPORT_COLUMNS) {
      if (col === "QR" && !showQr) continue;
      if (col === "Links" && !showLink) continue;
      row[col] = full[col];
    }
    return row as InventoryExportRow;
  });
}

// RFC-4180 cell escaping: wrap in quotes and double any embedded quotes when the
// value contains a comma, quote, or newline.
function escapeCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// Serialize an array of flat string-record rows to a CSV string. Column order
// follows the keys of the first row.
export function rowsToCsv(rows: Record<string, string>[]): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const lines = [headers.map(escapeCell).join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => escapeCell(row[h] ?? "")).join(","));
  }
  return lines.join("\r\n");
}

// Build a CSV string from rows and trigger a browser download via a Blob URL.
// A UTF-8 BOM is prepended so Excel opens accented characters correctly.
export function exportToCsv(rows: Record<string, string>[], filename: string): void {
  const csv = rowsToCsv(rows);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Today's date as YYYY-MM-DD for filenames.
export function exportDateStamp(d: Date = new Date()): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Turn an arbitrary context label (event name, account email, search term) into
// a filesystem-friendly slug.
export function slugifyForFilename(s: string): string {
  const slug = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug || "export";
}

// Compose the final "<base>-<date>.csv" download name.
export function inventoryExportFilename(base: string): string {
  return `${slugifyForFilename(base)}-${exportDateStamp()}.csv`;
}

// ---------------------------------------------------------------------------
// Accounts CSV export
// ---------------------------------------------------------------------------

const ACCOUNT_EXPORT_COLUMNS = [
  "Email",
  "Name",
  "Puller",
  "Phone",
  "Phone Status",
  "Bank Status",
  "AMP Match",
  "TM Password",
  "Active Inv Count",
] as const;

export type AccountExportRow = Record<(typeof ACCOUNT_EXPORT_COLUMNS)[number], string>;

function accountBankStatusLabel(account: Account): string {
  if (!account.bankMappingStatus) return "No Bank";
  return account.bankCardName || account.bankName || account.bankSource || "Bank";
}

export function buildAccountsExportRows(accounts: Account[]): AccountExportRow[] {
  return accounts.map((a) => ({
    Email: a.vendorAccountEmail,
    Name: a.accountName || a.vendorAccountName || "",
    Puller: a.pullerName || "",
    Phone: a.wrPhone || "",
    "Phone Status": a.phoneStatus || "unknown",
    "Bank Status": accountBankStatusLabel(a),
    "AMP Match": a.ampMatch ? "Yes" : "No",
    "TM Password": a.tmPassword || "",
    "Active Inv Count": String(a.activeInventoryCount ?? 0),
  }));
}

export function accountsExportFilename(): string {
  return `accounts-${exportDateStamp()}.csv`;
}

// ---------------------------------------------------------------------------
// Bank accounts CSV export
// ---------------------------------------------------------------------------

const BANK_ACCOUNT_EXPORT_COLUMNS = [
  "Key",
  "Card Name",
  "Source",
  "Bank",
  "Type",
  "Routing",
  "Account",
  "Slots Used",
  "Capacity",
  "Free Variants",
] as const;

export type BankAccountExportRow = Record<
  (typeof BANK_ACCOUNT_EXPORT_COLUMNS)[number],
  string
>;

// Routing/account number label mirroring the Banking table's CopyableNumber:
// the full number when the caller can see it (operators), otherwise the masked
// "••1234" last-4 form, so the export never shows more than the screen did.
function bankNumberLabel(full?: string | null, last4?: string | null): string {
  if (full) return full;
  if (last4) return `••${last4}`;
  return "";
}

export function buildBankAccountsExportRows(
  accounts: BankAccount[],
): BankAccountExportRow[] {
  return accounts.map((a) => ({
    Key: a.bankAccountKey,
    "Card Name": a.cardName || "",
    Source: a.source || "",
    Bank: a.bank || "",
    Type: a.accountType || "",
    Routing: bankNumberLabel(a.routingNumber, a.routingNumberLast4),
    Account: bankNumberLabel(a.accountNumber, a.accountNumberLast4),
    "Slots Used": String(a.allocationCount ?? 0),
    Capacity: String(a.capacity ?? 0),
    "Free Variants": (a.availableVariants ?? []).join(" "),
  }));
}

export function bankAccountsExportFilename(): string {
  return `bank-accounts-${exportDateStamp()}.csv`;
}
